// src/components/ui/PWAInstallButton.jsx

import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { Button } from './Button';

export function PWAInstallButton() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const ua = window.navigator.userAgent;
    const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    setIsIOS(iOS);

    // Check if already installed (this is an approximation)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capture install prompt
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Handle installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (isIOS) {
      setShowIOSInstructions(!showIOSInstructions);
    } else if (installPrompt) {
      // Show the install prompt
      installPrompt.prompt();
      // Wait for the user to respond to the prompt
      installPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
        }
        // Clear the saved prompt as it can't be used again
        setInstallPrompt(null);
      });
    }
  };

  // If already installed or no install prompt available and not iOS, don't show the button
  if ((isInstalled || (!installPrompt && !isIOS))) {
    return null;
  }

  return (
    <div className="relative">
      <Button 
        onClick={handleInstallClick}
        variant="outline"
        className="flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        {isIOS ? 'Install App' : 'Add to Home Screen'}
      </Button>
      
      {/* iOS Install Instructions */}
      {isIOS && showIOSInstructions && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg z-50 p-4 text-sm border border-gray-200">
          <h4 className="font-medium mb-2">How to install on iOS:</h4>
          <ol className="list-decimal pl-5 space-y-1">
            <li>
              Tap the share button 
              <svg className="inline-block ml-1 h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 12V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 3V16M12 3L8 7M12 3L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg> 
               in Safari
            </li>
            <li>Scroll down and tap "Add to Home Screen"</li>
            <li>Tap "Add" in the top-right corner</li>
          </ol>
          <div className="mt-2 text-xs text-gray-500">This will install the app on your home screen for quick access.</div>
        </div>
      )}
    </div>
  );
}